import React from 'react';
import styled from 'styled-components';
import { Text, InlineAlert, KeyValueTable, KeyValueRow, KeyCell, ValueCell, Flex, Icon } from '@amzn/storm-ui';
import { spinner } from '@amzn/storm-ui-icons';
import { useStrings } from '@flexible-reports/pages/shared/report-cost-estimation/report-cost-estimation.strings';
import { formatRowCountBucket, formatFileSizeBucket } from '@flexible-reports/utils/report-cost-estimation-api-utils';

const StyledText = styled(Text)`
  line-height: ${({ theme }) => theme.font.lineHeight.text.medium.default};
`;

const TableValueText = styled(StyledText)`
  text-align: right;
  white-space: nowrap;
`;

const Container = styled(Flex)`
  width: 100%;
  gap: ${({ theme }) => theme.spacing.base.default};
`;

const StyledKeyValueTable = styled(KeyValueTable)`
  background: none;
  border: none;
  width: 100%;

  && td,
  && th {
    background: none;
    border: none;
    padding: ${({ theme }) => theme.spacing.xxsmall.default};
  }
`;

const LoadingDisplay = ({ strings }: { strings: any }) => (
  <>
    <Icon type={spinner} size='sm' /> <span>{strings.calculating}</span>
  </>
);

interface DisplayValueProps {
  isLoading: boolean;
  isError: boolean;
  hasRequiredFields: boolean;
  hasSelectedAccounts: boolean;
  value: number | undefined;
  formatFunction: (value: number) => string;
  calculatingText: string;
}

const DisplayValue: React.FC<DisplayValueProps> = ({
  isLoading,
  isError,
  hasRequiredFields,
  hasSelectedAccounts,
  value,
  formatFunction,
  calculatingText
}) => {
  if (isLoading && hasRequiredFields && hasSelectedAccounts) {
    return <LoadingDisplay strings={{ calculating: calculatingText }} />;
  }
  if (isError || !hasRequiredFields || !hasSelectedAccounts) {
    return <>—</>;
  }
  if (value !== undefined) {
    return <>{formatFunction(value)}</>;
  }

  return <>—</>;
};

interface RowSizeDisplayProps {
  isLoading: boolean;
  isError: boolean;
  hasRequiredFields: boolean;
  hasSelectedAccounts: boolean;
  estimatedRowCount: number | undefined;
  calculatingText: string;
}

const RowSizeDisplay: React.FC<RowSizeDisplayProps> = props => (
  <DisplayValue {...props} value={props.estimatedRowCount} formatFunction={formatRowCountBucket} />
);

interface FileSizeDisplayProps {
  isLoading: boolean;
  isError: boolean;
  hasRequiredFields: boolean;
  hasSelectedAccounts: boolean;
  estimatedSizeBytes: number | undefined;
  calculatingText: string;
}

const FileSizeDisplay: React.FC<FileSizeDisplayProps> = props => (
  <DisplayValue {...props} value={props.estimatedSizeBytes} formatFunction={formatFileSizeBucket} />
);

const million = 1000000;
const getWarningMessage = (hasSelectedAccounts: boolean, hasRequiredFields: boolean, reportEstimate: any, strings: any): string => {
  if (!hasSelectedAccounts || !hasRequiredFields) {
    return strings.selectRequiredFields;
  }

  if (reportEstimate?.warningMessage) return reportEstimate.warningMessage;

  const isRowSizeOver1M = reportEstimate?.estimatedRowCount > million;
  const isApproachingThreshold = reportEstimate?.statusCode === 'APPROACHING_THRESHOLD';

  if (isRowSizeOver1M && isApproachingThreshold) {
    return strings.rowSizeOver1MApproachingThreshold;
  }

  if (isRowSizeOver1M && reportEstimate?.statusCode !== 'OVER_THRESHOLD') {
    return strings.rowSizeOver1M;
  }

  if (isApproachingThreshold) {
    return strings.approachingThresholdWarning;
  }

  return '';
};

interface ReportCostEstimationProps {
  data?: any;
  isLoading: boolean;
  isError: boolean;
  hasSelectedAccounts: boolean;
  hasRequiredFields: boolean;
  isOverThreshold: boolean;
}

export const ReportCostEstimation = ({
  data,
  isLoading,
  isError,
  hasSelectedAccounts,
  hasRequiredFields,
  isOverThreshold
}: ReportCostEstimationProps) => {
  const strings = useStrings();
  const reportEstimate = hasRequiredFields && hasSelectedAccounts ? data?.success?.[0]?.reportEstimate : undefined;
  const shouldShowApiErrorMessage = (isError || (data && !data.success)) && !isLoading;
  const warningMessage = shouldShowApiErrorMessage
    ? ''
    : getWarningMessage(hasSelectedAccounts, hasRequiredFields, reportEstimate, strings);
  const shouldShowErrorAlert = warningMessage !== '' && !shouldShowApiErrorMessage && !isOverThreshold;
  const shouldShowThresholdAlert = isOverThreshold && !shouldShowApiErrorMessage && !isLoading;

  return (
    <Container direction='column'>
      <StyledKeyValueTable caption=''>
        <KeyValueRow>
          <KeyCell>
            <StyledText>{strings.rowSize}</StyledText>{' '}
          </KeyCell>
          <ValueCell>
            <TableValueText>
              <RowSizeDisplay
                isLoading={isLoading}
                isError={isError || shouldShowApiErrorMessage}
                hasRequiredFields={hasRequiredFields}
                hasSelectedAccounts={hasSelectedAccounts}
                estimatedRowCount={reportEstimate?.estimatedRowCount}
                calculatingText={strings.calculating}
              />
            </TableValueText>
          </ValueCell>
        </KeyValueRow>
        <KeyValueRow>
          <KeyCell>
            <StyledText>{strings.fileSize}</StyledText>{' '}
          </KeyCell>
          <ValueCell>
            <TableValueText>
              <FileSizeDisplay
                isLoading={isLoading}
                isError={isError || shouldShowApiErrorMessage}
                hasRequiredFields={hasRequiredFields}
                hasSelectedAccounts={hasSelectedAccounts}
                estimatedSizeBytes={reportEstimate?.estimatedSizeBytes}
                calculatingText={strings.calculating}
              />
            </TableValueText>
          </ValueCell>
        </KeyValueRow>
      </StyledKeyValueTable>
      {shouldShowErrorAlert && <InlineAlert variant='warning' message={warningMessage} />}
      {shouldShowApiErrorMessage && <InlineAlert variant='error' message={strings.loadingError} />}
      {shouldShowThresholdAlert && <InlineAlert variant='error' message={`${strings.overThresholdAlert}`} />}
    </Container>
  );
};
